import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { OptimizerJob } from "../../services/optimizerService";
import { useBacktestStore } from "../../stores/backtestStore";

interface OptimizerJobCardProps {
  job: OptimizerJob;
  onDelete?: (jobId: string) => void;
  onRetry?: (jobId: string) => void;
}

export default function OptimizerJobCard({
  job,
  onDelete,
  onRetry,
}: OptimizerJobCardProps) {
  const navigate = useNavigate();

  // Use strategy_name from API, fall back to parsing source_dsl
  const strategyName = job.strategy_name
    || job.source_dsl?.match(/^strategy\s+"([^"]+)"/)?.[1]
    || "Unnamed Strategy";

  // Extract target from request_payload
  const targetMetric = job.request_payload?.target_metric || "";
  const targetValue = job.request_payload?.target_value || 0;
  const maxGenerations = job.request_payload?.max_generations || 20;
  const maxEvaluations = job.request_payload?.max_evaluations || 400;

  // Progress calculation
  const progress =
    maxEvaluations > 0 && job.evaluations_run
      ? Math.min(
          100,
          Math.round((job.evaluations_run / maxEvaluations) * 100),
        )
      : 0;

  // Elapsed time — prefer execution_ms from API, fall back to date math
  const elapsed = useMemo(() => {
    if (job.execution_ms) {
      const seconds = Math.floor(job.execution_ms / 1000);
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}m ${secs}s`;
    }
    const start = new Date(job.created_at).getTime();
    const end = job.completed_at
      ? new Date(job.completed_at).getTime()
      : Date.now();
    if (isNaN(start) || isNaN(end)) return "";
    const seconds = Math.floor((end - start) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  }, [job.created_at, job.completed_at, job.execution_ms]);

  // Best candidate metrics
  const bestCandidate = job.candidates?.[0];

  // Status visual
  const statusConfig = {
    pending: {
      icon: "🕐",
      color: "text-surface-400",
      bg: "bg-surface-700/30",
    },
    running: {
      icon: "🔄",
      color: "text-blue-400",
      bg: "bg-blue-500/10 border-blue-500/20",
    },
    completed: {
      icon: "✅",
      color: "text-green-400",
      bg: "bg-green-500/10 border-green-500/20",
    },
    failed: {
      icon: "❌",
      color: "text-red-400",
      bg: "bg-red-500/10 border-red-500/20",
    },
  }[job.status];

  return (
    <div
      className={`rounded-xl border p-5 transition-all ${statusConfig.bg} ${
        job.status === "completed"
          ? "hover:border-green-500/40 cursor-pointer"
          : ""
      } ${job.status === "running" ? "border-blue-500/30" : "border-surface-700/50"}`}
      onClick={() => {
        if (job.status === "completed" || job.status === "running") {
          navigate(`/optimizer/jobs/${job.job_id}`);
        }
      }}
    >
      {/* Status + Date */}
      <div className="flex items-center justify-between mb-3">
        <div className={`flex items-center gap-2 ${statusConfig.color}`}>
          <span>{statusConfig.icon}</span>
          <span className="text-sm font-medium capitalize">{job.status}</span>
        </div>
        <span className="text-xs text-surface-500">
          {new Date(job.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>
      </div>

      {/* Strategy Name */}
      <h3 className="text-lg font-semibold text-surface-100 mb-1">
        {strategyName}
      </h3>

      {/* Target */}
      <p className="text-sm text-surface-400 mb-3">
        Target: {targetMetric.toUpperCase()} → {targetValue}%
      </p>

      {/* Running: Progress Bar */}
      {job.status === "running" && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-surface-400 mb-1">
            <span>
              Gen {job.generations_run || 0}/{maxGenerations} ·{" "}
              {job.evaluations_run || 0}/{maxEvaluations} evals
            </span>
            <span>{elapsed}</span>
          </div>
          <div className="w-full bg-surface-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-right text-xs text-surface-500 mt-1">
            {progress}%
          </div>
        </div>
      )}

      {/* Completed: Metrics (best candidate or source) */}
      {job.status === "completed" && (
        <div className="mb-3 p-3 rounded-lg bg-surface-800/50">
          <div className="text-xs text-surface-500 mb-2">
            {bestCandidate
              ? `${job.candidates?.length || 0} candidates · ${elapsed}`
              : `No improved candidates · ${elapsed}`}
          </div>
          {(() => {
            const m = bestCandidate?.metrics || (job.source_metrics ? {
              cagr: job.source_metrics.cagr,
              sharpe: job.source_metrics.sharpe,
              max_drawdown: job.source_metrics.max_drawdown,
            } : null);
            if (!m) return null;
            return (
              <>
                {!bestCandidate && (
                  <div className="text-xs text-surface-500 mb-1">Original metrics:</div>
                )}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-sm font-bold metric-value-positive">
                      {m.cagr.toFixed(1)}%
                    </div>
                    <div className="text-xs text-surface-500">CAGR</div>
                  </div>
                  <div className="border-x border-surface-700/50">
                    <div className="text-sm font-bold text-surface-200">
                      {m.sharpe.toFixed(2)}
                    </div>
                    <div className="text-xs text-surface-500">Sharpe</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold metric-value-negative">
                      {m.max_drawdown.toFixed(1)}%
                    </div>
                    <div className="text-xs text-surface-500">Max DD</div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Failed: Error */}
      {job.status === "failed" && job.error_message && (
        <div className="mb-3 p-3 rounded-lg bg-red-500/10 text-red-400 text-sm">
          {job.error_message}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        {(job.status === "completed" || job.status === "failed") && job.source_dsl && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              useBacktestStore.getState().setStrategyCode(job.source_dsl!);
              navigate("/backtest");
            }}
            className="px-3 py-1.5 text-xs font-medium text-surface-300 border border-surface-600 rounded-lg hover:bg-surface-700/50 hover:text-surface-100"
          >
            Backtest
          </button>
        )}
        {job.status === "completed" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/optimizer/jobs/${job.job_id}`);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-primary-500/20 text-primary-400 border border-primary-500/30 rounded-lg hover:bg-primary-500/30"
          >
            View Results
          </button>
        )}
        {job.status === "running" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/optimizer/jobs/${job.job_id}`);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/30"
          >
            View Live
          </button>
        )}
        {job.status === "failed" && onRetry && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRetry(job.job_id);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/30"
          >
            Retry
          </button>
        )}
        {(job.status === "completed" || job.status === "failed") &&
          onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(job.job_id);
              }}
              className="px-3 py-1.5 text-xs font-medium text-surface-400 hover:text-red-400 transition"
            >
              Delete
            </button>
          )}
      </div>
    </div>
  );
}
